import React from "react";
import BlogImage from "../assets/blognew1.png";
import loginBG from "../assets/loginBG.png";

const features = [
  {
    title: "Designed for in-room guest experience",
    desc: "Guests can browse the full room service menu comfortably from their phone, without calling reception or waiting for printed menus.",
  },
  {
    title: "No apps, no learning curve",
    desc: "Guests simply scan a QR code placed in the room. No app installs, no sign-ups, no confusion.",
  },
  {
    title: "Room service powered by WhatsApp",
    desc: "All guest requests and food orders are delivered directly to WhatsApp, making it easy for hotel staff to respond quickly.",
  },
  {
    title: "Reduces reception & staff workload",
    desc: "Routine calls for menus, prices, and availability are eliminated, freeing staff to focus on guest service.",
  },
  {
    title: "Update menus in real time",
    desc: "Change room service items, prices, or availability instantly without reprinting menus.",
  },
  {
    title: "Visual menus increase upsells",
    desc: "High-quality images and clear categories encourage guests to explore more items and increase average order value.",
  },
  {
    title: "Professional QR placement for rooms",
    desc: "Clean QR codes designed for bedside tables, desks, TVs, and welcome folders.",
  },
  {
    title: "Centralized order visibility",
    desc: "Track all room service orders and guest requests from WhatsApp and a single dashboard.",
  },
];

export default function HotelInRoomDiningSystem() {
  return (
    <div className="bg-white text-gray-800 overflow-hidden">

      {/* HERO – IMMERSIVE */}
      <section
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.65), rgba(0,0,0,0.3)), url(${loginBG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center text-white">
          <div>
            <span className="inline-block mb-4 text-sm uppercase tracking-wider text-[#6cbb23]">
              Hotel In-Room Dining
            </span>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              A smarter way for guests to order, and hotels to earn more
            </h1>

            <p className="mt-6 text-lg text-gray-200">
              Zafflo modernizes in-room dining and guest requests using QR menus
              and WhatsApp — reducing friction while unlocking new revenue
              opportunities.
            </p>

            <div className="mt-10 flex gap-4">
              <a
                href="/signup"
                className="rounded-xl bg-[#6cbb23] px-6 py-3 font-semibold text-white hover:opacity-90"
              >
                Get Started Free
              </a>
              <a
                href="/demo"
                className="rounded-xl border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10"
              >
                Request Demo
              </a>
            </div>
          </div>

          <div className="hidden lg:block">
            <img
              src={BlogImage}
              alt="In Room Dining"
              className="w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* REALITY CHECK – SINGLE FRAMED BOX */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900">
            The reality of traditional in-room dining
          </h2>

          <div className="mt-14 rounded-3xl bg-white p-12 shadow-lg">
            <ul className="space-y-6">
              {[
                "Guests depend on reception calls, causing delays and frustration",
                "Printed menus go out of date and require frequent reprints",
                "Peak-hour calls overload staff and slow service",
                "Static menus limit upsells and discovery",
                "Guest requests get scattered across calls and notes",
                "Service quality varies depending on staff handling",
              ].map((item, idx) => (
                <li key={idx} className="flex gap-4">
                  <span className="mt-2 h-2 w-2 rounded-full bg-[#6cbb23]" />
                  <p className="text-gray-700">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* GUEST JOURNEY */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Designed around the guest journey
          </h2>

          <div className="mt-20 grid md:grid-cols-4 gap-10">
            {[
              ["Scan QR", "Guest scans the QR inside the room"],
              ["Browse", "Views menu, services & experiences"],
              ["Order", "Places request in seconds"],
              ["Fulfill", "Staff receives order instantly"],
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-[#6cbb23]/10 text-[#6cbb23] flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <h3 className="mt-4 font-semibold">{step[0]}</h3>
                <p className="mt-2 text-sm text-gray-600">{step[1]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVENUE STRIP */}
      <section className="py-28 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            More than room service — a revenue platform
          </h2>

          <p className="mt-6 text-center text-gray-300 max-w-3xl mx-auto">
            Hotels using Zafflo turn every guest interaction into an opportunity
            to increase per-stay revenue.
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {[
              "Airport transfers & local taxis",
              "Spa, wellness & housekeeping",
              "Local tours & experiences",
              "Partnered nearby businesses",
              "Late checkout & room upgrades",
            ].map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl bg-white/5 p-6 text-center"
              >
                <p className="text-gray-200">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Built for real hotel operations
          </h2>

          <div className="mt-20 grid md:grid-cols-3 gap-12">
            {features.map((f, i) => (
              <div key={i}>
                <h3 className="font-semibold text-lg">{f.title}</h3>
                <p className="mt-3 text-gray-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

          {/* CTA */}
      <section className="py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">
            Upgrade your in-room dining experience
          </h2>
          <p className="mt-4 text-gray-600">
            Deliver better guest experiences and unlock new revenue streams
            with Zafflo.
          </p>

          <a
            href="/signup"
            className="mt-8 inline-block rounded-xl bg-[#6cbb23] px-8 py-4 text-white font-semibold"
          >
            Start Free with Zafflo
          </a>
        </div>
      </section>
    </div>
  );
}
